import {
  BasePiaQuestionnaire,
  DataElementsQuestionnaire,
  DataRetentionQuestionnaire,
  DataSubjectsQuestionnaire,
  PiaFile,
  PiaInputType,
  PiaProcess,
  PiaQuestion,
  PiaRiskOption,
  RetentionPeriod,
  SafeguardsQuestionnaire,
  TicketStatus,
  TransfersQuestionnaire
} from './piaSlice'
import {
  PiaFieldNames,
  SYSTEM_TOPICS,
  TotalQuestionsEvaluatorMapper,
  getCustomQuestionsAndAnswersCount,
  getFieldProperty,
  removeDuplicates
} from './piaUtil'
import { RopaQuestionnaireTopicNames } from '../../constants'
import dayjs from 'dayjs'

export const deepClone = (obj) => typeof obj === 'object' && JSON.parse(JSON.stringify(obj))

export const getCompactCollaboratorDetails = (ropaCollaboration) => {
  const isFieldAssigned = (properties) => getFieldProperty('isAssigned', properties)[0] === 'true'
  const mapQuestionIds = (topic) =>
    topic.questions?.edges?.map(({ node }) => node.processQuestionId) || []
  return ropaCollaboration?.edges?.map(({ node }) => {
    const processDetails = node.ropaTopics?.processDetails
    const dataElements = node.ropaTopics?.dataElements
    const dataSubjects = node.ropaTopics?.dataSubjects
    const dataRetention = node.ropaTopics?.dataRetention
    const safeguards = node.ropaTopics?.safeguards
    const transfers = node.ropaTopics?.transfers

    const customTopics = node.ropaTopics?.customTopics || []
    const customQuestionIds: Set<string> = new Set([])
    customTopics.forEach((customTopic) => {
      const ids = mapQuestionIds(customTopic)
      ids.forEach((id) => {
        customQuestionIds.add(id)
      })
    })

    const riskTopics = node.ropaTopics?.riskTopics || []
    const riskCustomQuestionIds: Set<string> = new Set([])
    riskTopics.forEach((customTopic) => {
      mapQuestionIds(customTopic).forEach((id) => riskCustomQuestionIds.add(id))
    })

    const topicKeysToExclude: string[] = [
      RopaQuestionnaireTopicNames.customTopics,
      RopaQuestionnaireTopicNames.riskTopics
    ]

    const data = {
      id: node.id || '',
      name: node.name || '',
      email: node.emailId || '',
      parentCollaboratorId: node?.parentCollaboratorId || '',
      status: node.status || '',
      createdAt: node.createdAt || '',
      updateTimestamp: node.updateTimestamp || '',
      totalSections:
        Object.keys(node.ropaTopics || {}).filter(
          (key) => node.ropaTopics?.[key] && !topicKeysToExclude.includes(key)
        ).length +
        (node?.ropaTopics?.customTopics?.length || 0) +
        (node?.ropaTopics?.riskTopics?.length || 0),
      totalQuestions: 0,
      processDetailsQuestionIds: processDetails
        ? [
            ...mapQuestionIds(processDetails),
            ...(isFieldAssigned(processDetails.nameProperties) ? [PiaFieldNames.name] : []),
            ...(isFieldAssigned(processDetails.descriptionProperties)
              ? [PiaFieldNames.description]
              : []),
            ...(isFieldAssigned(processDetails.processGroupsProperties)
              ? [PiaFieldNames.processGroups]
              : []),
            ...(isFieldAssigned(processDetails.automatedProcessingProperties)
              ? [PiaFieldNames.automatedProcessing]
              : []),
            ...(isFieldAssigned(processDetails.automatedProcessingDescriptionProperties)
              ? [PiaFieldNames.automatedProcessingDescription]
              : []),
            ...(isFieldAssigned(processDetails.purposeProperties) ? [PiaFieldNames.purpose] : []),
            ...(isFieldAssigned(processDetails.lawfulBasisProperties)
              ? [PiaFieldNames.lawfulBasis]
              : []),
            ...(isFieldAssigned(processDetails.processDetailsProperties)
              ? [PiaFieldNames.processDetails]
              : [])
          ]
        : [],
      dataElementsQuestionIds: dataElements
        ? [
            ...mapQuestionIds(dataElements),
            ...(isFieldAssigned(dataElements.attributeProperties) ? ['attribute'] : [])
          ]
        : [],
      dataSubjectsQuestionIds: dataSubjects
        ? [
            ...mapQuestionIds(dataSubjects),
            ...(isFieldAssigned(dataSubjects.dataSubjectProperties) ? ['dataSubjects1'] : []),
            ...(isFieldAssigned(dataSubjects.specialDataSubjectProperties)
              ? ['dataSubjects2']
              : []),
            ...(isFieldAssigned(dataSubjects.specialDataSubjectsProperties)
              ? ['dataSubjects3']
              : []),
            ...(isFieldAssigned(dataSubjects.legalBasisProperties) ? ['dataSubjects4'] : [])
          ]
        : [],
      dataRetentionQuestionIds: dataRetention
        ? [
            ...mapQuestionIds(dataRetention),
            ...(isFieldAssigned(dataRetention.dataRetentionProperties) ? ['dataRetention'] : [])
          ]
        : [],
      safeguardsQuestionIds: safeguards
        ? [
            ...mapQuestionIds(safeguards),
            ...(isFieldAssigned(safeguards.safeguardProperties) ? ['safeguards'] : [])
          ]
        : [],
      transfersQuestionIds: transfers
        ? [
            ...mapQuestionIds(transfers),
            ...(isFieldAssigned(transfers.thirdParty.thirdPartyProperties)
              ? ['thirdParty-transfers']
              : []),
            ...(isFieldAssigned(transfers.crossBorder.crossBorderProperties)
              ? ['crossBorder-transfers']
              : [])
          ]
        : [],
      customQuestionIds: [...customQuestionIds],
      riskCustomQuestionIds: [...riskCustomQuestionIds],
      topics: {
        processDetails,
        dataElements,
        dataSubjects,
        dataRetention,
        safeguards,
        transfers,
        customTopics:
          customTopics?.map((topic) => ({
            optional: false,
            name: topic.name || '',
            questions:
              topic.questions?.edges?.map(({ node }) => ({
                questionId: node.questionId || '',
                question: node.question || '',
                isMandatory: node.isMandatory || false
              })) || []
          })) || []
      },
      riskTopics:
        riskTopics?.map((topic) => ({
          optional: false,
          name: topic.name || '',
          questions:
            topic.questions?.edges?.map(({ node }) => ({
              questionId: node.questionId || '',
              question: node.question || '',
              isMandatory: node.isMandatory || false
            })) || []
        })) || []
    }

    data.totalQuestions =
      +data.processDetailsQuestionIds?.length +
      +data.dataElementsQuestionIds?.length +
      +data.dataSubjectsQuestionIds?.length +
      +data.dataRetentionQuestionIds?.length +
      +data.safeguardsQuestionIds?.length +
      +data.transfersQuestionIds?.length +
      +data.customQuestionIds?.length +
      +data.riskCustomQuestionIds?.length

    return data
  })
}

const filterSubmittedTickets = (t) => t && t?.collaboratorDetails?.status === TicketStatus.Submitted
const calculateCollatedRisk = (questions, riskOptions: PiaRiskOption[]) => {
  const riskIds = new Set<string>(questions.map(({ systemRiskValue = '' }) => systemRiskValue))
  let order = -1
  riskIds.forEach((id) => {
    const riskOptionOrder = riskOptions?.find(({ id: riskId }) => riskId === id)?.value || 0
    if (riskOptionOrder > order) {
      order = riskOptionOrder
    }
  })
  return riskOptions.find(({ value }) => value === order)?.id || ''
}

const collateCustomQuestionResponse = (
  collaboratorTopics: any[],
  customQuestions: PiaQuestion[],
  riskOptions?: PiaRiskOption[]
): PiaQuestion[] => {
  if (!collaboratorTopics || !customQuestions) return []
  let clonedQuestions = [...customQuestions]

  const collatedQuestions = collaboratorTopics
    ?.filter(filterSubmittedTickets)
    ?.reduce((questions, topic) => {
      questions = [...questions, ...(topic.questions?.edges?.map((edge) => edge?.node) || [])]
      return questions
    }, [])

  const addCollaboratorQuestionIdsToFiles = (files?: PiaFile[], questionId?: string) => {
    return files?.map((f) => ({ ...f, questionId: f.questionId || questionId })) || []
  }

  clonedQuestions = clonedQuestions.map((question) => {
    const questionsInCollatedList = collatedQuestions.filter((q) => {
      const isSameByQuestionId = question?.questionId === q?.processQuestionId
      const isSameByCustomId = question?.customId === q?.customId
      return (isSameByQuestionId || isSameByCustomId) && question?.responseType === q?.responseType
    })

    question.options = removeDuplicates([
      ...(question.options || []),
      ...(questionsInCollatedList.map((q) => q?.options) || []).flat()
    ])

    question.questionResponse = removeDuplicates([
      ...(question.questionResponse || []),
      ...(questionsInCollatedList.map((q) => q?.questionResponse) || []).flat()
    ])

    if (question.responseType === PiaInputType.File) {
      const allSources = [question, ...questionsInCollatedList]

      // Determine which user (source) has the most recent upload
      const latestSource = allSources.reduce((latest, current) => {
        const getLatestFileDate = (q: PiaQuestion) => {
          if (!q.files || q.files.length === 0) return dayjs(0)
          return q.files.reduce((max, f) => {
            const currentFileDate = dayjs(f.uploadedAt)
            return currentFileDate.isAfter(max) ? currentFileDate : max
          }, dayjs(0))
        }

        return getLatestFileDate(current).isAfter(getLatestFileDate(latest)) ? current : latest
      }, question)

      // Replace the entire files array with the latest user's specific set
      question.files = addCollaboratorQuestionIdsToFiles(
        latestSource.files || [],
        question.questionId
      )
    }

    if (question.responseType === PiaInputType.SingleChoice) {
      question.questionResponse = question.questionResponse?.slice(0, 1)
    }

    question.systemRiskValue = calculateCollatedRisk(
      [question, ...questionsInCollatedList],
      riskOptions || []
    )
    return question
  })

  return clonedQuestions
}

const collateCustomTopicsResponse = (
  customTopics: BasePiaQuestionnaire[],
  collaboratorTopics: any[],
  riskOptions
): BasePiaQuestionnaire[] => {
  if (!collaboratorTopics || !collaboratorTopics.length) return customTopics
  return customTopics.map((topic) => {
    topic.questions = collateCustomQuestionResponse(
      collaboratorTopics,
      topic.questions || [],
      riskOptions
    )
    return topic
  })
}

export const collateProcessDetailsResponse = (
  process: PiaProcess,
  collaboratorTopics: any[]
): PiaProcess => {
  if (!collaboratorTopics || !collaboratorTopics.length) return process
  const collateTextInput = (accumulator: string, input: string): string => {
    return accumulator.includes(input) ? '' : (accumulator ? ', ' : '') + input
  }
  const collatedAnswers = collaboratorTopics.filter(filterSubmittedTickets)?.reduce(
    (answers, topic) => {
      answers.automatedProcessing = topic?.automatedProcessing ?? answers.automatedProcessing
      answers.description += collateTextInput(answers.description, topic?.description)
      answers.automatedProcessingDescription += collateTextInput(
        answers.automatedProcessingDescription,
        topic?.automatedProcessingDescription
      )
      answers.processGroups = [...(answers.processGroups || []), ...(topic?.processGroupIds || [])]
      answers.purpose = [...(answers.purpose || []), ...(topic?.purposeIds || [])]
      answers.lawfulBasis = [...(answers.lawfulBasis || []), ...(topic?.lawfulBasisIds || [])]

      return answers
    },
    {
      description: process.description || '',
      processGroups: process.processGroups || [],
      purpose: process.purpose || [],
      lawfulBasis: process.lawfulBasis || [],
      automatedProcessing: process.automatedProcessing || false,
      automatedProcessingDescription: process.automatedProcessingDescription || ''
      // processDetails: parentTopic.processDetails || '',
    }
  )

  process.description = collatedAnswers.description
  process.automatedProcessing = collatedAnswers.automatedProcessing
  process.automatedProcessingDescription = collatedAnswers.automatedProcessingDescription
  process.processGroups = removeDuplicates(collatedAnswers.processGroups)
  process.purpose = removeDuplicates(collatedAnswers.purpose)
  process.lawfulBasis = removeDuplicates(collatedAnswers.lawfulBasis)

  if (process.questionnaire?.processDetails) {
    process.questionnaire.processDetails.processQuestions = collateCustomQuestionResponse(
      collaboratorTopics,
      process.questionnaire.processDetails.processQuestions || []
    )
    process.questionnaire.processDetails.collaboratorDetails = collaboratorTopics.map(
      (t) => t.collaboratorDetails
    )
  }

  return process
}

export const collateDataElementsResponse = (
  parentTopic: DataElementsQuestionnaire,
  collaboratorTopics: any[]
): DataElementsQuestionnaire => {
  if (!collaboratorTopics || !collaboratorTopics.length) return parentTopic
  const clonedTopic = { ...parentTopic }

  const collatedAnswers = collaboratorTopics.filter(filterSubmittedTickets)?.reduce(
    (answers, topic) => {
      answers.attributes = [
        ...answers.attributes,
        ...(topic.attributes?.edges?.map(({ node }) => node.id) || [])
      ]
      answers.lawfulBases = [
        ...answers.lawfulBases,
        ...(topic.lawfulBases?.edges?.map(({ node }) => node.id) || [])
      ]
      answers.specialElements = [
        ...answers.specialElements,
        ...(topic.specialElements?.map((specialElement) => ({
          attributeName: specialElement.attributeName,
          sensitivity: specialElement.sensitivity
        })) || [])
      ]
      answers.associatedDatasources = [
        ...answers.associatedDatasources,
        ...(topic.associatedDatasources?.edges?.map(({ node }) => node.id) || [])
      ]
      return answers
    },
    {
      attributes: parentTopic.attributes || [],
      lawfulBases: parentTopic.lawfulBases || [],
      specialElements: parentTopic.specialElements || [],
      associatedDatasources: parentTopic.associatedDatasources || []
    }
  )

  clonedTopic.attributes = removeDuplicates(collatedAnswers.attributes)
  clonedTopic.lawfulBases = removeDuplicates(collatedAnswers.lawfulBases)
  clonedTopic.specialElements = removeDuplicates(collatedAnswers.specialElements, 'attributeName')
  clonedTopic.associatedDatasources = removeDuplicates(collatedAnswers.associatedDatasources)
  clonedTopic.questions = collateCustomQuestionResponse(
    collaboratorTopics,
    clonedTopic.questions || []
  )
  clonedTopic.collaboratorDetails = collaboratorTopics.map((t) => t.collaboratorDetails)

  return clonedTopic
}

export const collateDataSubjectsResponse = (
  parentTopic: DataSubjectsQuestionnaire,
  collaboratorTopics: any[]
): DataSubjectsQuestionnaire => {
  if (!collaboratorTopics || !collaboratorTopics.length) return parentTopic
  const clonedTopic = { ...parentTopic }

  const collatedAnswers = collaboratorTopics.filter(filterSubmittedTickets)?.reduce(
    (answers, topic) => {
      answers.categories = [
        ...answers.categories,
        ...(topic?.dataSubjects?.edges?.map(({ node }) => node.id) || [])
      ]
      answers.lawfulBasis = [
        ...answers.lawfulBasis,
        ...(topic.legalBases?.edges?.map(({ node }) => node.id) || [])
      ]
      answers.specialCategories = [
        ...answers.specialCategories,
        ...(topic.specialDataSubjects?.edges?.map(({ node }) => node.id) || [])
      ]
      answers.specialDataSubject = topic?.specialDataSubject || false
      return answers
    },
    {
      categories: parentTopic.categories || [],
      specialCategories: parentTopic.specialCategories || [],
      lawfulBasis: parentTopic.lawfulBasis || [],
      specialDataSubject: parentTopic.specialDataSubject || false
    }
  )

  clonedTopic.categories = removeDuplicates(collatedAnswers.categories)
  clonedTopic.lawfulBasis = removeDuplicates(collatedAnswers.lawfulBasis)
  clonedTopic.specialCategories = removeDuplicates(collatedAnswers.specialCategories)
  clonedTopic.specialDataSubject = collatedAnswers.specialDataSubject
  clonedTopic.questions = collateCustomQuestionResponse(
    collaboratorTopics,
    clonedTopic.questions || []
  )
  clonedTopic.collaboratorDetails = collaboratorTopics.map((t) => t.collaboratorDetails)
  return clonedTopic
}

export const collateDataRetentionResponse = (
  parentTopic: DataRetentionQuestionnaire,
  collaboratorTopics: any[]
): DataRetentionQuestionnaire => {
  if (!collaboratorTopics || !collaboratorTopics.length) return parentTopic
  const clonedTopic = { ...parentTopic }

  const collatedAnswers = collaboratorTopics.filter(filterSubmittedTickets)?.reduce(
    (answers, topic) => {
      answers.details = [
        ...answers.details,
        ...(topic?.dataRetentionInfo?.edges?.map(({ node }) => ({
          id: node.id,
          durationType: node.durationType,
          durationCount: node.durationCount,
          triggerEvent: node.triggerEvent,
          attributeSets: node.attributeSet?.edges?.map(({ node }) => node.id) || []
        })) || [])
      ]

      return answers
    },
    {
      details: parentTopic.details || []
    }
  )

  const getUniqueDetails = (details: RetentionPeriod[]): RetentionPeriod[] => {
    const uniqueDetails: RetentionPeriod[] = []
    const detailSet = new Set()

    details.forEach((detail) => {
      const detailKey = JSON.stringify({ ...detail, id: undefined })
      if (!detailSet.has(detailKey)) {
        detailSet.add(detailKey)
        uniqueDetails.push(detail)
      }
    })

    const getFilledForm = (form: RetentionPeriod) => {
      return (
        form?.durationType ||
        form.durationCount !== undefined ||
        form?.triggerEvent ||
        !!form?.attributeSets?.length
      )
    }

    const filledForms = uniqueDetails.filter(getFilledForm)
    return filledForms.length > 0 ? filledForms : uniqueDetails.slice(0, 1)
  }

  clonedTopic.details = getUniqueDetails(collatedAnswers.details)
  clonedTopic.questions = collateCustomQuestionResponse(
    collaboratorTopics,
    clonedTopic.questions || []
  )

  clonedTopic.collaboratorDetails = collaboratorTopics.map((t) => t.collaboratorDetails)

  return clonedTopic
}

export const collateSafeguardsResponse = (
  parentTopic: SafeguardsQuestionnaire,
  collaboratorTopics: any[]
): SafeguardsQuestionnaire => {
  if (!collaboratorTopics || !collaboratorTopics.length) return parentTopic
  const clonedTopic = { ...parentTopic }

  const collatedAnswers = collaboratorTopics.filter(filterSubmittedTickets)?.reduce(
    (answers, topic) => {
      answers.safeguards = [
        ...answers.safeguards,
        ...(topic.safeguards?.edges?.map(({ node }) => node.id) || [])
      ]

      return answers
    },
    {
      safeguards: parentTopic.safeguards || []
    }
  )

  clonedTopic.safeguards = removeDuplicates(collatedAnswers.safeguards)
  clonedTopic.questions = collateCustomQuestionResponse(
    collaboratorTopics,
    clonedTopic.questions || []
  )
  clonedTopic.collaboratorDetails = collaboratorTopics.map((t) => t.collaboratorDetails)

  return clonedTopic
}

export const collateTransfersResponse = (
  parentTopic: TransfersQuestionnaire,
  collaboratorTopics: any[]
): TransfersQuestionnaire => {
  if (!collaboratorTopics || !collaboratorTopics.length) return parentTopic
  const clonedTopic = { ...parentTopic }

  const collatedAnswers = {
    thirdParty: {
      isThirdParty: parentTopic.thirdParty.isThirdParty || false, // answer will be selected based on metaData time
      details: parentTopic.thirdParty.details || [],
      submitTime: (() => {
        const meta = parentTopic.thirdParty?.metaData ?? []
        return meta.length > 0 ? meta[meta.length - 1].submittedAt : 0
      })()
    },
    crossBorder: {
      isCrossBorder: parentTopic.crossBorder.isCrossBorder || false, // answer will be selected based on metaData time
      details: parentTopic.crossBorder.details || [],
      submitTime: (() => {
        const meta = parentTopic.crossBorder?.metaData ?? []
        return meta.length > 0 ? meta[meta.length - 1].submittedAt : 0
      })()
    }
  }

  collaboratorTopics.filter(filterSubmittedTickets)?.forEach((topic) => {
    // third party collation
    const thirdPartyMeta = topic.thirdParty?.metaData ?? []
    const thirdPartySubmitTime =
      thirdPartyMeta.length > 0 ? thirdPartyMeta[thirdPartyMeta.length - 1].submittedAt : 0
    if (dayjs(thirdPartySubmitTime).isAfter(dayjs(collatedAnswers.thirdParty.submitTime))) {
      collatedAnswers.thirdParty.isThirdParty = topic?.thirdParty?.isThirdParty
      collatedAnswers.thirdParty.submitTime = thirdPartySubmitTime
    }

    collatedAnswers.thirdParty.details = [
      ...collatedAnswers.thirdParty.details,
      ...(topic?.thirdParty?.isThirdParty
        ? topic?.thirdParty?.details?.map((detail) => ({
            safeguards: detail.safeguards?.edges?.map(({ node }) => node.id),
            safeguardsProperties: detail.safeguardsProperties,
            email: detail.email,
            emailProperties: detail.emailProperties,
            name: detail.name,
            nameProperties: detail.nameProperties,
            address: detail.address,
            addressProperties: detail.addressProperties
          })) || []
        : [])
    ]

    // cross border collation
    const crossBorderMeta = topic.crossBorder?.metaData ?? []
    const crossBorderSubmitTime =
      crossBorderMeta.length > 0 ? crossBorderMeta[crossBorderMeta.length - 1].submittedAt : 0
    if (dayjs(crossBorderSubmitTime).isAfter(dayjs(collatedAnswers.crossBorder.submitTime))) {
      collatedAnswers.crossBorder.isCrossBorder = topic?.crossBorder?.isCrossBorder
      collatedAnswers.crossBorder.submitTime = crossBorderSubmitTime
    }

    collatedAnswers.crossBorder.details = [
      ...collatedAnswers.crossBorder.details,
      ...(topic?.crossBorder?.isCrossBorder
        ? topic?.crossBorder?.details?.map((detail) => ({
            safeguards: detail.safeguards?.edges?.map(({ node }) => node.id),
            safeguardsProperties: detail.safeguardsProperties,
            email: detail.email,
            emailProperties: detail.emailProperties,
            organisation: detail.organisation,
            organisationProperties: detail.organisationProperties,
            country: detail.country,
            countryProperties: detail.countryProperties
          })) || []
        : [])
    ]
  })

  const getUniqueDetails = (details) => {
    const uniqueDetails: any = []
    const detailSet = new Set()

    details.forEach((detail) => {
      const detailKey = JSON.stringify(detail)
      if (!detailSet.has(detailKey)) {
        detailSet.add(detailKey)
        uniqueDetails.push(detail)
      }
    })

    const getFilledForm = (form) => {
      return (
        form.email ||
        !!form.safeguards?.length ||
        form.name ||
        form.address ||
        form.organisation ||
        form.country
      )
    }

    const filledForms = uniqueDetails.filter(getFilledForm)
    return filledForms.length > 0 ? filledForms : uniqueDetails.slice(0, 1)
  }

  clonedTopic.thirdParty.isThirdParty = collatedAnswers.thirdParty.isThirdParty
  clonedTopic.crossBorder.isCrossBorder = collatedAnswers.crossBorder.isCrossBorder
  clonedTopic.thirdParty.details = getUniqueDetails(collatedAnswers.thirdParty.details)
  clonedTopic.crossBorder.details = getUniqueDetails(collatedAnswers.crossBorder.details)
  clonedTopic.collaboratorDetails = collaboratorTopics.map((t) => t.collaboratorDetails)
  clonedTopic.questions = collateCustomQuestionResponse(
    collaboratorTopics,
    clonedTopic.questions || []
  )

  return clonedTopic
}

export const collateCollaboratorResponses = (
  process: PiaProcess,
  node,
  riskOptions
): PiaProcess => {
  if (!node?.edges) return process

  let processClone = deepClone(process)

  const allTopics = node?.edges?.reduce(
    (topics, { node }) => {
      if (node?.ropaTopics?.processDetails) {
        topics.allProcessDetailsTopics = [
          ...topics.allProcessDetailsTopics,
          {
            ...node?.ropaTopics?.processDetails,
            collaboratorDetails: {
              email: node?.emailId,
              status: node?.status
            }
          }
        ]
      }
      if (node?.ropaTopics?.dataElements) {
        topics.allDataElementsTopics = [
          ...topics.allDataElementsTopics,
          {
            ...node?.ropaTopics?.dataElements,
            collaboratorDetails: {
              email: node?.emailId,
              status: node?.status
            }
          }
        ]
      }
      if (node?.ropaTopics?.dataSubjects) {
        topics.allDataSubjectTopics = [
          ...topics.allDataSubjectTopics,
          {
            ...node?.ropaTopics?.dataSubjects,
            collaboratorDetails: {
              email: node?.emailId,
              status: node?.status
            }
          }
        ]
      }
      if (node?.ropaTopics?.dataRetention) {
        topics.allDataRetentionTopics = [
          ...topics.allDataRetentionTopics,
          {
            ...node?.ropaTopics?.dataRetention,
            collaboratorDetails: {
              email: node?.emailId,
              status: node?.status
            }
          }
        ]
      }
      if (node?.ropaTopics?.safeguards) {
        topics.allSafeguardsTopics = [
          ...topics.allSafeguardsTopics,
          {
            ...node?.ropaTopics?.safeguards,
            collaboratorDetails: {
              email: node?.emailId,
              status: node?.status
            }
          }
        ]
      }
      if (node?.ropaTopics?.transfers) {
        topics.allTransfersTopics = [
          ...topics.allTransfersTopics,
          {
            ...node?.ropaTopics?.transfers,
            collaboratorDetails: {
              email: node?.emailId,
              status: node?.status
            }
          }
        ]
      }
      if (node?.ropaTopics?.customTopics.length) {
        topics.allCustomTopics = [
          ...topics.allCustomTopics,
          ...node?.ropaTopics?.customTopics.map((topic) => ({
            ...topic,
            collaboratorDetails: {
              email: node?.emailId,
              status: node?.status
            }
          }))
        ]
      }
      if (node?.ropaTopics?.riskTopics.length) {
        topics.allRiskTopics = [
          ...topics.allRiskTopics,
          ...node?.ropaTopics?.riskTopics.map((topic) => ({
            ...topic,
            collaboratorDetails: {
              email: node?.emailId,
              status: node?.status
            }
          }))
        ]
      }

      return topics
    },
    {
      allProcessDetailsTopics: [],
      allDataElementsTopics: [],
      allDataSubjectTopics: [],
      allDataRetentionTopics: [],
      allSafeguardsTopics: [],
      allTransfersTopics: [],
      allCustomTopics: [],
      allRiskTopics: []
    }
  )
  const {
    allProcessDetailsTopics = [],
    allDataElementsTopics = [],
    allDataSubjectTopics = [],
    allDataRetentionTopics = [],
    allSafeguardsTopics = [],
    allTransfersTopics = [],
    allCustomTopics = [],
    allRiskTopics = []
  } = allTopics
  if (processClone.questionnaire?.processDetails) {
    processClone = collateProcessDetailsResponse(
      processClone,
      allProcessDetailsTopics.filter((t) => t)
    )
  }
  if (processClone.questionnaire?.dataElements) {
    processClone.questionnaire.dataElements = collateDataElementsResponse(
      processClone.questionnaire.dataElements,
      allDataElementsTopics.filter((t) => t)
    )
  }
  if (processClone.questionnaire?.dataSubjects) {
    processClone.questionnaire.dataSubjects = collateDataSubjectsResponse(
      processClone.questionnaire.dataSubjects,
      allDataSubjectTopics.filter((t) => t)
    )
  }
  if (processClone.questionnaire?.dataRetention) {
    processClone.questionnaire.dataRetention = collateDataRetentionResponse(
      processClone.questionnaire.dataRetention,
      allDataRetentionTopics.filter((t) => t)
    )
  }
  if (processClone.questionnaire?.safeguards) {
    processClone.questionnaire.safeguards = collateSafeguardsResponse(
      processClone.questionnaire.safeguards,
      allSafeguardsTopics.filter((t) => t)
    )
  }
  if (processClone.questionnaire?.transfers) {
    processClone.questionnaire.transfers = collateTransfersResponse(
      processClone.questionnaire.transfers,
      allTransfersTopics.filter((t) => t)
    )
  }
  if (processClone.questionnaire?.customTopics) {
    processClone.questionnaire.customTopics = collateCustomTopicsResponse(
      processClone.questionnaire.customTopics,
      allCustomTopics.filter((t) => t),
      riskOptions
    )
  }
  if (processClone.riskDetails?.riskTopics) {
    processClone.riskDetails.riskTopics = collateCustomTopicsResponse(
      processClone.riskDetails?.riskTopics,
      allRiskTopics.filter((t) => t),
      riskOptions
    )
  }

  return processClone
}

export const getTicketOrProcessStats = (
  process: PiaProcess,
  isTicket?: boolean
): { questions: number; answers: number; status: TicketStatus } => {
  let questions = 0
  let answers = 0
  const topics = [...SYSTEM_TOPICS]
  topics.forEach((topicKey) => {
    if (process.questionnaire?.[topicKey]) {
      const { totalQuestions, totalQuestionsAnswered } = TotalQuestionsEvaluatorMapper[topicKey](
        process,
        isTicket !== undefined ? isTicket : true
      )
      questions += totalQuestions
      answers += totalQuestionsAnswered
    }
  })

  if (process.questionnaire?.customTopics?.length) {
    process.questionnaire.customTopics.forEach((topic) => {
      const { answers: cAnswers, questions: cQuestions } = getCustomQuestionsAndAnswersCount(
        topic.questions
      )
      questions += cQuestions
      answers += cAnswers
    })
  }

  // TODO risk topics: add error handling for risk topics in UI
  if (process.riskDetails?.riskTopics?.length) {
    process.riskDetails.riskTopics.forEach((topic) => {
      const { answers: cAnswers, questions: cQuestions } = getCustomQuestionsAndAnswersCount(
        topic.questions
      )
      questions += cQuestions
      answers += cAnswers
    })
  }

  if (answers < questions) return { status: TicketStatus.InProgress, questions, answers }
  if (answers === questions) return { status: TicketStatus.Submitted, questions, answers }

  return { status: TicketStatus.NotStarted, questions, answers }
}
